import * as THREE from 'three'
import { registry } from '../components-lib/registry'
import { resolveUniformScaledParams } from '../utils/uniformScaleParams'
import type { SceneManager } from './SceneManager'
import { getDropWorldPosition } from './DragHandler'
import { ownMaterialsInObject } from './MaterialFactory'
import { resolvePlacement, wantsSurfaceSnap } from '../utils/surfaceSnap'
import { getThreeObject } from './ObjectManager'
import type { SceneObject } from '../types/scene'

const GHOST_OPACITY = 0.4

/**
 * 拖放 Ghost 预览：从 ComponentTray 拖出组件时，在鼠标落点显示半透明预览。
 * 半透明只作用在 Ghost 自己的材质副本上，绝不写回预设或其它对象。
 */
export class DragGhostPreview {
  private _sm: SceneManager | null = null
  private _ghost: THREE.Group | null = null
  private _currentType: string | null = null
  private _lastValidWorldPos: THREE.Vector3 | null = null

  setSceneManager(sm: SceneManager | null) {
    this._sm = sm
  }

  /**
   * 在指定屏幕坐标处显示 Ghost（用于 dragstart 时在 Canvas 边缘待命）
   * @param type 组件类型
   * @param clientX 屏幕 X
   * @param clientY 屏幕 Y
   * @param yElevation 当前楼层 Y 偏移（米），默认 0
   */
  showAtPosition(type: string, clientX: number, clientY: number, yElevation = 0, objects: SceneObject[] = []): boolean {
    const fakeEvent = { clientX, clientY } as DragEvent
    return this.update(type, fakeEvent, yElevation, objects)
  }

  /**
   * 拖拽中：更新 Ghost 位置
   * @param type 组件类型
   * @param event DragEvent 或包含 clientX/clientY 的对象
   * @param yElevation 当前楼层 Y 偏移（米），默认 0
   * @returns 是否成功更新（若画布/相机不可用则 false）
   */
  update(
    type: string,
    event: { clientX: number; clientY: number; ctrlKey?: boolean; metaKey?: boolean },
    yElevation = 0,
    objects: SceneObject[] = [],
  ): boolean {
    if (!this._sm) return false
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    const hasUsableScreenPoint =
      Number.isFinite(event.clientX) &&
      Number.isFinite(event.clientY) &&
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom

    let worldPos: THREE.Vector3 | null = null
    if (hasUsableScreenPoint) {
      const placed = resolvePlacement(event, canvas, this._sm.camera, {
        floorY: yElevation,
        scene: this._sm.scene,
        objects,
        getThreeObject,
        snapToSurfaces: wantsSurfaceSnap(event),
        type, // 铺地件按脚下建筑地面吸附，避免共面
      })
      if (placed) {
        worldPos = new THREE.Vector3(placed.x, placed.y, placed.z)
        this._lastValidWorldPos = worldPos.clone()
      } else {
        worldPos = getDropWorldPosition(event as DragEvent, canvas, this._sm.camera, { groundY: yElevation })
        if (worldPos) this._lastValidWorldPos = worldPos.clone()
      }
    }
    if (!worldPos && this._lastValidWorldPos) {
      worldPos = this._lastValidWorldPos.clone()
    }
    if (!worldPos) {
      worldPos = new THREE.Vector3(
        Math.round(this._sm.controls.target.x),
        yElevation,
        Math.round(this._sm.controls.target.z)
      )
    }

    if (this._ghost && this._currentType !== type) {
      this.dispose()
    }

    if (!this._ghost) {
      const def = registry.get(type)
      if (!def) return false
      const defaultParams: Record<string, any> = {}
      for (const p of def.params) defaultParams[p.key] = p.default
      const previewGroup = def.create(resolveUniformScaledParams(def, defaultParams))
      // 先收成私有材质，再改透明度——切断对模块级/预设的引用
      ownMaterialsInObject(previewGroup)
      previewGroup.traverse((child) => {
        if (!(child instanceof THREE.Mesh) || !child.material) return
        const src = child.material
        const ghostify = (mat: THREE.Material) => {
          const cloned = mat.clone()
          cloned.transparent = true
          cloned.opacity = GHOST_OPACITY
          cloned.depthWrite = false
          cloned.userData = { ...mat.userData, _cached: false, _preset: false, _instanceOwned: true }
          return cloned
        }
        child.material = Array.isArray(src) ? src.map(ghostify) : ghostify(src)
      })
      previewGroup.userData.isGhost = true
      this._sm.scene.add(previewGroup)
      this._ghost = previewGroup
      this._currentType = type
    }

    this._ghost.position.set(worldPos.x, worldPos.y, worldPos.z)
    // SceneManager 为按需渲染：Ghost 的显隐/位置变化后必须主动标记重绘。
    this._ghost.visible = true
    this._sm.markDirty()
    return true
  }

  dispose() {
    if (this._ghost && this._sm) {
      this._sm.scene.remove(this._ghost)
      this._ghost.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry && !child.geometry.userData._sharedGeo) child.geometry.dispose()
          const mats = Array.isArray(child.material) ? child.material : [child.material]
          mats.forEach((mat) => {
            if (mat && !mat.userData?._cached && typeof mat.dispose === 'function') mat.dispose()
          })
        }
      })
      this._ghost = null
      this._sm.markDirty()
    }
    this._currentType = null
    this._lastValidWorldPos = null
  }
}
